<!-- 
 * 收益计算器悬浮按钮组件
 * 专门用于吸引用户打开收益计算器
 * 具有强烈的视觉吸引力和动画效果
 -->
<template>
  <div>
    <!-- 吸睛的计算器悬浮按钮 - 始终展开状态，可拖拽 -->
    <div 
      ref="draggableBtn"
      class="calculator-floating-btn"
      :style="style"
      @click="handleClick"
    >
      <!-- 闪烁的光圈效果 -->
      <div class="pulse-ring"></div>
      <div class="pulse-ring pulse-ring-delay"></div>
      
      <!-- 按钮主体 -->
      <div class="btn-content">
        <div class="icon-wrapper">
          <SvgIcon icon="heroicons:calculator" class="calculator-icon" />
        </div>
        <div class="btn-text">
          <div class="main-text">{{ $t('calculator.attractiveText') }}</div>
          <div class="sub-text">{{ $t('calculator.attractiveSubText') }}</div>
        </div>
      </div>

      <!-- 闪烁的"NEW"标签 -->
      <div class="new-badge">
        <span class="badge-text">{{ $t('calculator.badge') }}</span>
      </div>
      
      <!-- 拖拽提示图标 -->
      <div class="drag-hint">
        <SvgIcon icon="heroicons:arrows-pointing-out" />
      </div>
    </div>

    <!-- 收益计算器弹窗 -->
    <RevenueCalculator v-model="showCalculator" />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick } from 'vue';
import { useDraggable, useWindowSize } from '@vueuse/core';
import SvgIcon from '@/components/SvgIcon.vue';
import RevenueCalculator from '@/components/common/calculator/RevenueCalculator.vue';

const showCalculator = ref(false);
const draggableBtn = ref<HTMLElement | null>(null);

// 获取窗口尺寸
const { width: windowWidth, height: windowHeight } = useWindowSize();

// 固定的边距值
const FIXED_RIGHT_MARGIN = 20;
const FIXED_BOTTOM_MARGIN = 80;

// 初始位置（临时值，会在 onMounted 中更新）
const initialX = windowWidth.value - 320;
const initialY = windowHeight.value - 152;

// 使用 VueUse 的 useDraggable
const { x, y, style, isDragging } = useDraggable(draggableBtn, {
  initialValue: { x: initialX, y: initialY },
  preventDefault: true,
  stopPropagation: true,
});

/**
 * 组件挂载后，根据实际元素宽高重新计算位置
 */
onMounted(async () => {
  await nextTick();
  if (draggableBtn.value) {
    const rect = draggableBtn.value.getBoundingClientRect();
    const newX = windowWidth.value - rect.width - FIXED_RIGHT_MARGIN;
    const newY = windowHeight.value - rect.height - FIXED_BOTTOM_MARGIN;
    x.value = newX;
    y.value = newY;
  }
});

/**
 * 处理点击事件，打开计算器
 */
const handleClick = () => {
  if (!isDragging.value) {
    showCalculator.value = true;
  }
};
</script>

<style scoped lang="scss">
@use '@/styles/scss/mixins.scss';

.calculator-floating-btn {
  position: fixed;
  z-index: 1000;
  cursor: move;
  
  // 优化移动端触摸
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  
  // 始终展开状态
  width: auto;
  min-width: 220px;
  max-width: 300px;
  height: 64px;
  padding: 0 20px 0 72px;
  border-radius: 32px;
  
  // 深色渐变背景，确保在浅色背景下文字清晰
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  box-shadow: 0 8px 24px rgba(30, 41, 59, 0.5),
              0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  
  // 玻璃效果
  backdrop-filter: blur(8px);
  
  // 拖拽时的样式
  &:active {
    cursor: grabbing;
    transform: scale(1.05);
    box-shadow: 0 16px 40px rgba(30, 41, 59, 0.7);
  }
  
  &:hover {
    box-shadow: 0 12px 32px rgba(30, 41, 59, 0.6),
                0 0 0 1px rgba(255, 255, 255, 0.15) inset;
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  }
  
  // 按钮内容
  .btn-content {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    
    .icon-wrapper {
      position: absolute;
      left: -68px;
      top: 50%;
      transform: translateY(-50%);
      width: 64px;
      height: 64px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
      border-radius: 50%;
      box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
      
      .calculator-icon {
        font-size: 28px;
        color: white;
        animation: bounce-scale 2s ease-in-out infinite;
      }
    }
    
    .btn-text {
      color: white;
      white-space: nowrap;
      flex: 1;
      
      .main-text {
        font-size: 15px;
        font-weight: 700;
        line-height: 1.2;
        margin-bottom: 2px;
        color: #ffffff;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
      }
      
      .sub-text {
        font-size: 11px;
        color: rgba(255, 255, 255, 0.85);
        font-weight: 500;
      }
    }
  }
  
  // 脉冲光圈效果 - 调整到图标位置
  .pulse-ring {
    position: absolute;
    top: 50%;
    left: 4px;
    transform: translate(0, -50%);
    width: 64px;
    height: 64px;
    border-radius: 50%;
    border: 3px solid rgba(245, 158, 11, 0.6);
    animation: pulse-animation 2s cubic-bezier(0.455, 0.03, 0.515, 0.955) infinite;
    pointer-events: none;
    
    &.pulse-ring-delay {
      animation-delay: 1s;
      border-color: rgba(249, 115, 22, 0.5);
    }
  }
  
  // NEW 徽章
  .new-badge {
    position: absolute;
    top: -8px;
    right: -8px;
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    padding: 4px 10px;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.5);
    animation: badge-bounce 1.5s ease-in-out infinite;
    
    .badge-text {
      font-size: 10px;
      font-weight: 700;
      color: white;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }
  
  // 拖拽提示图标
  .drag-hint {
    position: absolute;
    top: 4px;
    left: 4px;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.5);
    font-size: 14px;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }
  
  &:hover .drag-hint {
    opacity: 1;
  }
}

// 图标弹跳缩放动画
@keyframes bounce-scale {
  0%, 100% {
    transform: scale(1) rotate(0deg);
  }
  25% {
    transform: scale(1.1) rotate(-5deg);
  }
  50% {
    transform: scale(0.95) rotate(0deg);
  }
  75% {
    transform: scale(1.05) rotate(5deg);
  }
}

// 脉冲光圈动画
@keyframes pulse-animation {
  0% {
    transform: translate(0, -50%) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(0, -50%) scale(1.5);
    opacity: 0;
  }
}

// 徽章弹跳动画
@keyframes badge-bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

/* 移动端适配 */
@media (max-width: 768px) {
  .calculator-floating-btn {
    min-width: 180px;
    height: 56px;
    padding: 0 16px 0 64px;
    border-radius: 28px;
    
    .btn-content {
      .icon-wrapper {
        left: -60px;
        width: 56px;
        height: 56px;
        
        .calculator-icon {
          font-size: 24px;
        }
      }
      
      .btn-text {
        .main-text {
          font-size: 13px;
        }
        
        .sub-text {
          font-size: 10px;
        }
      }
    }
    
    .pulse-ring {
      width: 56px;
      height: 56px;
    }
    
    .new-badge {
      top: -6px;
      right: -6px;
      padding: 3px 8px;
      
      .badge-text {
        font-size: 9px;
      }
    }
  }
}
</style>
